import { get, off, push, ref, remove, set, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { getPathDatabaseByRole } from "./auth";
import { db } from "../lib/firebase";
import { LaunchError } from "../utils/errors";
import { deleteFolderAndUnlinkContents } from "./folderDeletion";
import {
  COMPETITOR_FAVORITE_DESTINATION_ROOT,
  COMPETITOR_FOLDER_CHILDREN_ROOT,
  COMPETITOR_FOLDER_CONTENTS_ROOT,
  COMPETITOR_FOLDERS_ROOT,
  CONTENT_FOLDER_ROOT_ID,
} from "../utils/constants";
import {
  isDuplicateFolderName,
  normalizeFolderName,
  normalizeParentFolderID,
} from "../utils/contentFolderUtils";

const getCompetitorFoldersPath = ({ advertiserID }) =>
  `${COMPETITOR_FOLDERS_ROOT}/${advertiserID}`;

const getCompetitorFolderPath = ({ advertiserID, folderID }) =>
  `${getCompetitorFoldersPath({ advertiserID })}/${folderID}`;

const getCompetitorFolderChildrenPath = ({ advertiserID, parentFolderID }) =>
  `${COMPETITOR_FOLDER_CHILDREN_ROOT}/${advertiserID}/${parentFolderID}`;

const getCompetitorFolderContentsPath = ({ advertiserID }) =>
  `${COMPETITOR_FOLDER_CONTENTS_ROOT}/${advertiserID}`;

const getCompetitorFolderContentIndexPath = ({
  advertiserID,
  folderID,
  contentID,
}) =>
  `${COMPETITOR_FOLDER_CONTENTS_ROOT}/${advertiserID}/${folderID}/${contentID}`;

const getCompetitorFavoriteDestinationPath = ({ advertiserID }) =>
  `${COMPETITOR_FAVORITE_DESTINATION_ROOT}/${advertiserID}`;

export const buildCompetitorFolderMembershipMap = (contentsSnapshotVal) => {
  const membershipByContentID = {};

  Object.entries(contentsSnapshotVal || {}).forEach(([folderID, contents]) => {
    Object.keys(contents || {}).forEach((contentID) => {
      membershipByContentID[contentID] = folderID;
    });
  });

  return membershipByContentID;
};

export const startListenerCompetitorFolders =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getCompetitorFoldersPath({ advertiserID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || {});
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCompetitorFolders",
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCompetitorFolders =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startListenerCompetitorFolderContents =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getCompetitorFolderContentsPath({ advertiserID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(
          buildCompetitorFolderMembershipMap(snapshot.val() || {})
        );
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCompetitorFolderContents",
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCompetitorFolderContents =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startListenerCompetitorFavoriteDestination =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback(null);
        return false;
      }

      const dbRef = ref(
        db,
        getCompetitorFavoriteDestinationPath({ advertiserID })
      );
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || null);
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCompetitorFavoriteDestination",
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCompetitorFavoriteDestination =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startSetCompetitorFavoriteDestination =
  ({ folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const normalizedFolderID = String(folderID || "").trim();

      if (!advertiserID || !normalizedFolderID) {
        return { ok: false };
      }

      await set(
        ref(db, getCompetitorFavoriteDestinationPath({ advertiserID })),
        normalizedFolderID
      );

      return { ok: true, data: { folderID: normalizedFolderID } };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startSetCompetitorFavoriteDestination",
          context: { folderID },
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startClearCompetitorFavoriteDestination =
  () => async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        return { ok: false };
      }

      await remove(
        ref(db, getCompetitorFavoriteDestinationPath({ advertiserID }))
      );

      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startClearCompetitorFavoriteDestination",
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startCreateCompetitorFolder =
  ({ name, parentFolderID = CONTENT_FOLDER_ROOT_ID }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);
    const normalizedParentID = normalizeParentFolderID(parentFolderID);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const foldersPath = getCompetitorFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: normalizedParentID,
          name: normalizedName,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const folderID = push(ref(db, foldersPath)).key;
      const now = Date.now();
      const folder = {
        name: normalizedName,
        parentFolderID: normalizedParentID,
        creationTime: now,
        updatedTime: now,
      };

      await update(ref(db), {
        [getCompetitorFolderPath({ advertiserID, folderID })]: folder,
        [`${getCompetitorFolderChildrenPath({
          advertiserID,
          parentFolderID: normalizedParentID,
        })}/${folderID}`]: true,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          ...folder,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startCreateCompetitorFolder",
          context: { name: normalizedName, parentFolderID: normalizedParentID },
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startMoveCompetitorAdsToFolder =
  ({ contentIDs, folderID, folderMembershipByContentID = {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];

      if (!advertiserID || !folderID || uniqueContentIDs.length === 0) {
        return { ok: false };
      }

      const targetFolderSnapshot = await get(
        ref(db, getCompetitorFolderPath({ advertiserID, folderID }))
      );
      if (!targetFolderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      let membershipByContentID = folderMembershipByContentID;

      if (!Object.keys(membershipByContentID).length) {
        const contentsSnapshot = await get(
          ref(db, getCompetitorFolderContentsPath({ advertiserID }))
        );
        membershipByContentID = buildCompetitorFolderMembershipMap(
          contentsSnapshot.val()
        );
      }

      const updates = {};
      const movedContentIDs = [];

      uniqueContentIDs.forEach((contentID) => {
        const previousFolderID = membershipByContentID[contentID];
        if (previousFolderID === folderID) return;

        if (previousFolderID) {
          updates[
            getCompetitorFolderContentIndexPath({
              advertiserID,
              folderID: previousFolderID,
              contentID,
            })
          ] = null;
        }

        updates[
          getCompetitorFolderContentIndexPath({
            advertiserID,
            folderID,
            contentID,
          })
        ] = true;
        movedContentIDs.push(contentID);
      });

      if (movedContentIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          contentIDs: movedContentIDs,
          folderID,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startMoveCompetitorAdsToFolder",
          context: { contentIDs, folderID },
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startUnassignCompetitorAdsFromFolder =
  ({ contentIDs, folderMembershipByContentID = {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];

      if (!advertiserID || uniqueContentIDs.length === 0) {
        return { ok: false };
      }

      let membershipByContentID = folderMembershipByContentID;

      if (!Object.keys(membershipByContentID).length) {
        const contentsSnapshot = await get(
          ref(db, getCompetitorFolderContentsPath({ advertiserID }))
        );
        membershipByContentID = buildCompetitorFolderMembershipMap(
          contentsSnapshot.val()
        );
      }

      const updates = {};
      const unassignedContentIDs = [];

      uniqueContentIDs.forEach((contentID) => {
        const previousFolderID = membershipByContentID[contentID];
        if (!previousFolderID) return;

        updates[
          getCompetitorFolderContentIndexPath({
            advertiserID,
            folderID: previousFolderID,
            contentID,
          })
        ] = null;
        unassignedContentIDs.push(contentID);
      });

      if (unassignedContentIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          contentIDs: unassignedContentIDs,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUnassignCompetitorAdsFromFolder",
          context: { contentIDs },
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startRenameCompetitorFolder =
  ({ folderID, name }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !folderID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const folderSnapshot = await get(
        ref(db, getCompetitorFolderPath({ advertiserID, folderID }))
      );

      if (!folderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const currentFolder = folderSnapshot.val();
      const foldersPath = getCompetitorFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: currentFolder.parentFolderID,
          name: normalizedName,
          excludeFolderID: folderID,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const now = Date.now();
      await update(ref(db), {
        [`${getCompetitorFolderPath({ advertiserID, folderID })}/name`]:
          normalizedName,
        [`${getCompetitorFolderPath({ advertiserID, folderID })}/updatedTime`]:
          now,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          name: normalizedName,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRenameCompetitorFolder",
          context: { folderID, name: normalizedName },
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startDeleteCompetitorFolder =
  ({ folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      return await deleteFolderAndUnlinkContents({
        advertiserID,
        folderID,
        foldersRoot: COMPETITOR_FOLDERS_ROOT,
        childrenRoot: COMPETITOR_FOLDER_CHILDREN_ROOT,
        contentsRoot: COMPETITOR_FOLDER_CONTENTS_ROOT,
        getEntityPaths: () => [],
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteCompetitorFolder",
          context: { folderID },
          tags: { action: "competitorFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
